'use client';
import { Box, Link, Stack, Typography } from '@mui/material';
import { BRAND_GRADIENTS, BRAND_TONE, BrandTone, COLORS, RADIUS } from '../../constants';
import { FadeInUp } from '../fadeInUp';
import { MaterialIconType } from '../materialIcon/types';
import { StatusLottie } from '../statusLottie';

export type ResultScreenStatus = 'success' | 'error' | 'validating' | 'loading' | 'info';

// Same tints as customer-mobile's GradientStatusScreen, faded at the top of the card instead of the whole screen.
// The fade ends in lavender under the lifted white block, or in white when the actions follow directly
const TINTS: Record<Exclude<ResultScreenStatus, 'validating' | 'loading'>, string> = {
  success: `${COLORS.gradient.success[0]}26`,
  error: `${COLORS.gradient.error[0]}33`,
  // Brand tint for screens with their own badge (no matching mobile animation), e.g. the SMS link screen
  info: `${COLORS.brand.main}26`,
};
const headerBackground = (status: ResultScreenStatus, hasChildren: boolean, tone: BrandTone) => {
  // Wait screens use the brand glow, like customer-mobile's Loading
  if (status === 'validating' || status === 'loading') return BRAND_GRADIENTS.glow(tone);
  const end = hasChildren ? `${COLORS.background[0]}` : COLORS.surface.raised;
  return `linear-gradient(180deg, ${TINTS[status]} 0%, ${COLORS.background[0]} 60%, ${end} 100%)`;
};

/**
 * Result screen laid out like customer-mobile's success and rejection screens: status animation, optional support
 * code, title, description and WhatsApp help link, with web-only content and every action passed in by the caller.
 *
 * @param {ResultScreenStatus} status - Picks the animation and the header tint; 'validating' (identity check) and
 *   'loading' (any other wait) loop while waiting; 'info' shows `visual` instead of an animation.
 * @param {MaterialIconType} [icon] - Glyph in the 'loading' badge, hourglass by default.
 * @param {React.ReactNode} [visual] - Replaces the animation, e.g. an IconBadge.
 * @param {string} [code] - Support code shown above the title.
 * @param {React.ReactNode} title - Main message.
 * @param {React.ReactNode} [description] - Text under the title.
 * @param {string} [supportHref] - WhatsApp link; adds "¿Tenés dudas? Escribinos a nuestro WhatsApp".
 * @param {React.ReactNode} [children] - Extra content in the white block under the message.
 * @param {React.ReactNode} [actions] - Buttons, stacked at the bottom in the given order.
 * @param {BrandTone} [tone] - Brand of the wait screens' glow, the app's NEXT_PUBLIC_BRAND_TONE by default.
 * @return {React.JSX.Element} The rendered screen.
 */
export const ResultScreen = ({
  status,
  icon,
  visual,
  code,
  title,
  description,
  supportHref,
  children,
  actions,
  tone = BRAND_TONE,
}: {
  status: ResultScreenStatus;
  icon?: MaterialIconType;
  visual?: React.ReactNode;
  code?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  supportHref?: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  tone?: BrandTone;
}) => (
  <Stack
    data-testid="result-screen"
    // A wait screen is announced once and marked busy, like customer-mobile's Loading
    {...((status === 'validating' || status === 'loading') && {
      role: 'status',
      'aria-live': 'polite',
      'aria-busy': true,
    })}
    sx={{
      width: '100%',
      height: '100%',
      textAlign: 'center',
      borderRadius: { xs: 0, md: `${RADIUS.xxl + 4}px` },
      overflow: 'hidden',
      bgcolor: COLORS.surface.raised,
    }}
  >
    <Stack
      sx={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: { xs: 3, md: 8 },
        pt: { xs: 6, md: 8 },
        pb: children ? 8 : 4,
        background: headerBackground(status, !!children, tone),
      }}
    >
      {visual ?? (status !== 'info' && <StatusLottie type={status} icon={icon} />)}
      {code && (
        <FadeInUp index={0} sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ color: COLORS.text.main }}>
            {code}
          </Typography>
        </FadeInUp>
      )}
      <FadeInUp index={0} sx={{ mt: code ? 0 : 2 }}>
        <Typography variant="h1" component="h1">
          {title}
        </Typography>
      </FadeInUp>
      {description && (
        <FadeInUp index={1} sx={{ maxWidth: 480 }}>
          <Typography component="div" variant="lead">
            {description}
          </Typography>
        </FadeInUp>
      )}
      {supportHref && (
        <FadeInUp index={2} sx={{ mt: 1 }}>
          <Typography variant="lead">
            ¿Tenés dudas?
            <br />
            Escribinos a nuestro{' '}
            <Link
              href={supportHref}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 700, textDecoration: 'none' }}
            >
              {/* The apps' global a { color: inherit } rule outranks the Link color, a span keeps it purple */}
              <Box component="span" sx={{ color: COLORS.text.brand }}>
                WhatsApp
              </Box>
            </Link>
          </Typography>
        </FadeInUp>
      )}
    </Stack>
    {children && (
      // White block lifted over the tinted header, as the onboarding success screens already did
      <FadeInUp
        index={3}
        sx={{
          mt: -4,
          borderRadius: `${RADIUS.xxl + 4}px ${RADIUS.xxl + 4}px 0 0`,
          bgcolor: COLORS.surface.raised,
          px: { xs: 3, md: 8 },
          pt: 4,
        }}
      >
        {children}
      </FadeInUp>
    )}
    {actions && (
      <FadeInUp index={4}>
        <Stack sx={{ gap: 2, width: '100%', px: { xs: 3, md: 8 }, pt: 4, pb: { xs: 4, md: 8 } }}>{actions}</Stack>
      </FadeInUp>
    )}
  </Stack>
);
